import { getGlobalHeadersAsObject } from "..";

export interface PaginatedResponse<T> {
	count: number;
	next: string | null;
	previous: string | null;
	results: T[];
}

export interface LLMPersonAssignmentEval {
	id: number;
	person_id: number;
	person_name: string;
	assignment_id: number;
	assignment_name: string;
	is_match: boolean | null;
	score: number | null;
	requirement_score: number | null;
	llm_structured_response: any;
	model_name: string | null;
	requirement_source_id: number | null;
	source_gcq_type: string | null;
	created: string;
	updated: string;
}

export interface LLMPersonAssignmentEvalRequirementStat {
	requirement_id: number;
	text: string;
	mandatory: boolean;
	priority: number;
	yes: number;
	no: number;
	absent: number;
}

export interface LLMPersonAssignmentEvalExperienceBin {
	label: string;
	min: number;
	max: number;
	count: number;
}

export interface LLMPersonAssignmentEvalSeniorityBucket {
	level: string;
	count: number;
}

export interface LLMPersonAssignmentEvalScoreBin {
	label: string;
	min: number;
	max: number;
	count: number;
}

export interface LLMPersonAssignmentEvalAggregateAnalysis {
	assignment_id: number;
	total: number;
	match_counts: {
		match: number;
		no_match: number;
		unknown: number;
	};
	requirements: LLMPersonAssignmentEvalRequirementStat[];
	risk_flags: {
		overqualified: number;
		current_role_irrelevant: number;
		offered_role_irrelevant: number;
		out_of_league: number;
	};
	score: {
		bins: LLMPersonAssignmentEvalScoreBin[];
		absent: number;
	};
	experience: {
		bins: LLMPersonAssignmentEvalExperienceBin[];
		absent: number;
	};
	seniority: LLMPersonAssignmentEvalSeniorityBucket[];
	seniority_absent: number;
}

/** Light version without heavy llm_structured_response field */
export interface LLMPersonAssignmentEvalLight {
	id: number;
	person_id: number;
	person_name: string;
	is_match: boolean | null;
	score: number | null;
	requirement_score: number | null;
	model_name: string | null;
	requirement_source_id: number | null;
	source_gcq_type: string | null;
	created: string;
}

/** Debug info for LLMCache */
export interface LLMCacheDebug {
	id: number;
	duration_ms: number | null;
	model: string;
	prompt: string;
	response: string;
	created: string;
}

/** Debug info for RequirementSource */
export interface RequirementSourceDebug {
	id: number;
	source_type: "ai" | "manual";
	text: string;
	extractor_prompt_id: number | null;
	role_description_id: number | null;
	created: string;
	updated: string;
	created_by_id: number | null;
	updated_by_id: number | null;
}

/** Debug version with extended LLMCache and CV info */
export interface LLMPersonAssignmentEvalDebug extends LLMPersonAssignmentEval {
	llm_cache: LLMCacheDebug | null;
	requirement_source: RequirementSourceDebug | null;
	token_count: number | null;
	cost: number | null;
	cv_text_snapshot: string | null;
	cv_text_source: string | null;
}

export interface LLMPersonAssignmentEvalListParams {
	assignmentId: number;
	requirementSourceId?: number;
	page?: number;
	pageSize?: number;
	ordering?: string;
	search?: string;
	noPage?: boolean;
	isMatch?: boolean | null;
	riskFlags?: string[];
	reqStatuses?: string[];
}

const buildListUrl = (
	params: LLMPersonAssignmentEvalListParams,
	light: boolean
): URL => {
	const url = new URL("/api/v2/llm-person-assignment-eval/", window.location.origin);
	url.searchParams.append("assignment_id", params.assignmentId.toString());
	if (params.requirementSourceId) {
		url.searchParams.append(
			"requirement_source_id",
			params.requirementSourceId.toString()
		);
	}
	if (params.page) url.searchParams.append("page", params.page.toString());
	if (params.pageSize)
		url.searchParams.append("page_size", params.pageSize.toString());
	if (params.ordering) url.searchParams.append("ordering", params.ordering);
	if (params.search) url.searchParams.append("search", params.search);
	if (params.noPage) url.searchParams.append("no_page", "true");
	if (params.isMatch !== undefined && params.isMatch !== null) {
		url.searchParams.append("is_match", params.isMatch ? "true" : "false");
	}
	if (params.riskFlags && params.riskFlags.length > 0) {
		url.searchParams.append("risk_flag", params.riskFlags.join(","));
	}
	if (params.reqStatuses && params.reqStatuses.length > 0) {
		url.searchParams.append("req_status", params.reqStatuses.join(","));
	}
	if (light) url.searchParams.append("light", "true");
	return url;
};

export const getLLMPersonAssignmentEvals = async (
	params: LLMPersonAssignmentEvalListParams
): Promise<PaginatedResponse<LLMPersonAssignmentEval>> => {
	const url = buildListUrl(params, false);
	const res = await fetch(url.toString(), {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (!res.ok) {
		throw new Error(
			`Failed to fetch LLM person assignment evaluations: ${res.status}`
		);
	}
	return res.json();
};

/** Fetch ALL evaluations (no pagination) in light mode. Use only for cross-row aggregation. */
export const getAllLLMPersonAssignmentEvalsLight = async (
	assignmentId: number
): Promise<LLMPersonAssignmentEvalLight[]> => {
	const url = buildListUrl({ assignmentId, noPage: true }, true);
	const res = await fetch(url.toString(), {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (!res.ok) {
		throw new Error(
			`Failed to fetch LLM person assignment evaluations: ${res.status}`
		);
	}
	return res.json();
};

export interface LLMPersonAssignmentEvalAggregateParams {
	assignmentId: number;
	requirementSourceId?: number;
	isMatch?: boolean | null;
	riskFlags?: string[];
	reqStatuses?: string[];
}

export const getLLMPersonAssignmentEvalAggregateAnalysis = async (
	params: LLMPersonAssignmentEvalAggregateParams
): Promise<LLMPersonAssignmentEvalAggregateAnalysis> => {
	const url = new URL(
		"/api/v2/llm-person-assignment-eval/aggregate_analysis/",
		window.location.origin
	);
	url.searchParams.append("assignment_id", params.assignmentId.toString());
	if (params.requirementSourceId) {
		url.searchParams.append(
			"requirement_source_id",
			params.requirementSourceId.toString()
		);
	}
	if (params.isMatch !== undefined && params.isMatch !== null) {
		url.searchParams.append("is_match", params.isMatch ? "true" : "false");
	}
	if (params.riskFlags && params.riskFlags.length > 0) {
		url.searchParams.append("risk_flag", params.riskFlags.join(","));
	}
	if (params.reqStatuses && params.reqStatuses.length > 0) {
		url.searchParams.append("req_status", params.reqStatuses.join(","));
	}
	const res = await fetch(url.toString(), {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (!res.ok) {
		throw new Error(
			`Failed to fetch LLM person assignment eval aggregates: ${res.status}`
		);
	}
	return res.json();
};

/** Fetch evaluations created strictly after `since` for live streaming. */
export const getRecentLLMPersonAssignmentEvals = async (
	assignmentId: number,
	since: Date
): Promise<LLMPersonAssignmentEvalLight[]> => {
	const url = new URL(
		"/api/v2/llm-person-assignment-eval/recent/",
		window.location.origin
	);
	url.searchParams.append("assignment_id", assignmentId.toString());
	url.searchParams.append("since", since.toISOString());

	const res = await fetch(url.toString(), {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (!res.ok) {
		throw new Error(`Failed to fetch recent LLM evaluations: ${res.status}`);
	}
	const data = await res.json();
	if (Array.isArray(data)) {
		return data as LLMPersonAssignmentEvalLight[];
	}
	return [];
};

/** Fetch evaluation with debug info (LLMCache, CV text, tokens, cost) */
export const getLLMPersonAssignmentEvalDebug = async (
	personId: number,
	assignmentId: number
): Promise<LLMPersonAssignmentEvalDebug | null> => {
	const url = new URL("/api/v2/llm-person-assignment-eval/", window.location.origin);
	url.searchParams.append("person_id", personId.toString());
	url.searchParams.append("assignment_id", assignmentId.toString());
	url.searchParams.append("debug", "true");

	const res = await fetch(url.toString(), {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (!res.ok) {
		return null;
	}
	const data = await res.json();
	const results = Array.isArray(data) ? data : data?.results || [];
	return results.length > 0 ? results[0] : null;
};

/** One eval by id, with debug detail — also one a newer eval has replaced. */
export const getLLMPersonAssignmentEvalDebugById = async (
	evalId: number
): Promise<LLMPersonAssignmentEvalDebug | null> => {
	const url = new URL(
		`/api/v2/llm-person-assignment-eval/${evalId}/`,
		window.location.origin
	);
	url.searchParams.append("debug", "true");

	const res = await fetch(url.toString(), {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (!res.ok) {
		return null;
	}
	return (await res.json()) as LLMPersonAssignmentEvalDebug;
};
